<template>
  <v-app id="inspire" style="background:#F9F9F9;">
    <vue-header-admin></vue-header-admin>
    <v-main class="mb-10">
      <v-container fluid class="px-6 py-6">
        <!-- Header -->
        <v-row class="mb-6">
          <v-col cols="12" md="8">
            <h1 class="text-h4 font-weight-bold text-typo mb-4">
              <v-icon large color="primary" class="mr-3">mdi-gift</v-icon>
              Sweepstakes Management
            </h1>
            <p class="text-subtitle-1 grey--text">
              Manage sweepstakes campaigns, entry tiers, and settings
            </p>
          </v-col>
          <v-col cols="12" md="4" class="d-flex align-end justify-end">
            <v-btn
              color="green"
              dark
              large
              @click="openCreateDialog"
              class="elevation-2"
            >
              <v-icon left class="material-icons-round">add</v-icon>
              Create Campaign
            </v-btn>
          </v-col>
        </v-row>

        <!-- Stats Cards -->
        <v-row class="mb-6">
          <v-col cols="12" sm="6" md="3">
            <v-card class="card-shadow border-radius-xl">
              <v-card-text class="text-center pa-4">
                <v-icon size="40" color="primary" class="mb-2 material-icons-round">emoji_events</v-icon>
                <div class="text-h4 font-weight-bold">{{ stats.totalCampaigns }}</div>
                <div class="text-subtitle-2 grey--text">Total Campaigns</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="card-shadow border-radius-xl">
              <v-card-text class="text-center pa-4">
                <v-icon size="40" color="success" class="mb-2 material-icons-round">play_circle</v-icon>
                <div class="text-h4 font-weight-bold">{{ stats.activeCampaigns }}</div>
                <div class="text-subtitle-2 grey--text">Active Campaigns</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="card-shadow border-radius-xl">
              <v-card-text class="text-center pa-4">
                <v-icon size="40" color="orange" class="mb-2 material-icons-round">group</v-icon>
                <div class="text-h4 font-weight-bold">{{ stats.totalEntries }}</div>
                <div class="text-subtitle-2 grey--text">Total Entries</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="card-shadow border-radius-xl">
              <v-card-text class="text-center pa-4">
                <v-icon size="40" color="purple" class="mb-2 material-icons-round">attach_money</v-icon>
                <div class="text-h4 font-weight-bold">${{ formatNumber(stats.totalRevenue) }}</div>
                <div class="text-subtitle-2 grey--text">Total Revenue</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Referral Statistics -->
        <v-row class="mb-6">
          <v-col cols="12">
            <v-card class="card-shadow border-radius-xl">
              <v-card-title class="d-flex align-center">
                <v-icon color="orange" class="mr-2 material-icons-round">group</v-icon>
                <span class="text-h6 font-weight-bold">Referral Tracking Statistics</span>
              </v-card-title>

              <v-card-text>
                <v-row>
                  <v-col cols="12" sm="6" md="3">
                    <div class="text-center">
                      <div class="text-h4 font-weight-bold orange--text">{{ referralStats.total_referrals || 0 }}</div>
                      <div class="text-subtitle-2 grey--text">Total Referrals</div>
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6" md="3">
                    <div class="text-center">
                      <div class="text-h4 font-weight-bold success--text">{{ referralStats.signup_referrals || 0 }}</div>
                      <div class="text-subtitle-2 grey--text">Signup Referrals</div>
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6" md="3">
                    <div class="text-center">
                      <div class="text-h4 font-weight-bold info--text">{{ referralStats.entry_referrals || 0 }}</div>
                      <div class="text-subtitle-2 grey--text">Entry Referrals</div>
                    </div>
                  </v-col>

                  <v-col cols="12" sm="6" md="3">
                    <div class="text-center">
                      <div class="text-h4 font-weight-bold purple--text">${{ formatNumber(referralStats.total_referral_value || 0) }}</div>
                      <div class="text-subtitle-2 grey--text">Referral Value</div>
                    </div>
                  </v-col>
                </v-row>

                <!-- Top Referrers -->
                <v-divider class="my-4"></v-divider>
                <h4 class="mb-3">Top Referrers</h4>
                <v-simple-table v-if="referralStats.top_referrers && referralStats.top_referrers.length > 0">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Referral Code</th>
                        <th>Total Referrals</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="referrer in referralStats.top_referrers" :key="referrer.user_id">
                        <td>{{ referrer.first_name }} {{ referrer.last_name }}</td>
                        <td>{{ referrer.email }}</td>
                        <td>
                          <v-chip small color="orange" text-color="white">
                            {{ referrer.referral_code }}
                          </v-chip>
                        </td>
                        <td>{{ referrer.total_referrals }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
                <div v-else class="text-center py-4 text--secondary">
                  No referrals yet
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Target Price Progress Card -->
        <v-row class="mb-6" v-if="activeCampaignWithTarget">
          <v-col cols="12">
            <v-card class="card-shadow border-radius-xl">
              <v-card-title class="d-flex align-center">
                <v-icon color="success" class="mr-2 material-icons-round">track_changes</v-icon>
                <span class="text-h6 font-weight-bold">Target Price Progress</span>
                <v-spacer></v-spacer>
                <v-chip
                  :color="targetProgressColor"
                  text-color="white"
                  small
                >
                  {{ Math.round(targetProgressPercentage) }}%
                </v-chip>
              </v-card-title>

              <v-card-text>
                <div class="mb-4">
                  <div class="d-flex justify-space-between align-center mb-2">
                    <span class="text-subtitle-1 font-weight-medium">{{ activeCampaignWithTarget.title }}</span>
                    <span class="text-h6 font-weight-bold">
                      ${{ formatNumber(activeCampaignWithTarget.current_revenue || 0) }} / ${{ formatNumber(activeCampaignWithTarget.cape_price) }}
                    </span>
                  </div>

                  <v-progress-linear
                    :value="targetProgressPercentage"
                    :color="targetProgressColor"
                    height="20"
                    rounded
                    class="mb-2"
                  >
                    <template v-slot:default="{ value }">
                      <strong class="white--text">{{ Math.ceil(value) }}%</strong>
                    </template>
                  </v-progress-linear>

                  <div class="d-flex justify-space-between text-caption grey--text">
                    <span>Remaining: ${{ formatNumber(Math.max(0, activeCampaignWithTarget.cape_price - (activeCampaignWithTarget.current_revenue || 0))) }}</span>
                    <span v-if="targetProgressPercentage >= 100" class="success--text font-weight-bold">🎉 Target Reached!</span>
                    <span v-else>{{ Math.round(100 - targetProgressPercentage) }}% to go</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Campaigns Table -->
        <v-card class="card-shadow border-radius-xl">
          <v-card-title class="d-flex justify-space-between align-center">
            <div class="d-flex align-center">
              <v-icon color="primary" class="mr-2 material-icons-round">view_list</v-icon>
              <span class="text-h6 font-weight-bold">Sweepstakes Campaigns</span>
            </div>
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search campaigns..."
              single-line
              hide-details
              outlined
              dense
              class="mt-0 pt-0"
              style="max-width: 300px;"
            ></v-text-field>
          </v-card-title>

          <v-data-table
            :headers="headers"
            :items="campaigns"
            :search="search"
            :loading="loading"
            class="elevation-1"
            :items-per-page="10"
          >
          <!-- Status Column -->
          <template v-slot:item.status="{ item }">
            <v-chip
              :color="getStatusColor(item.status)"
              text-color="white"
              small
            >
              {{ item.status }}
            </v-chip>
          </template>

          <!-- Prize Value Column -->
          <template v-slot:item.prize_value="{ item }">
            <span class="font-weight-bold">${{ formatNumber(item.prize_value) }}</span>
          </template>

          <!-- Dates Column -->
          <template v-slot:item.start_date="{ item }">
            {{ formatDate(item.start_date) }}
          </template>

          <template v-slot:item.end_date="{ item }">
            {{ formatDate(item.end_date) }}
          </template>

          <!-- Entries Column -->
          <template v-slot:item.total_entries="{ item }">
            <v-chip color="orange" text-color="white" small>
              {{ item.total_entries || 0 }}
            </v-chip>
          </template>

          <!-- Actions Column -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex gap-1">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    @click="editCampaign(item)"
                  >
                    <v-icon small class="material-icons-round">edit</v-icon>
                  </v-btn>
                </template>
                <span>Edit Campaign</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="info"
                    v-bind="attrs"
                    v-on="on"
                    @click="manageTiers(item)"
                  >
                    <v-icon small class="material-icons-round">format_list_numbered</v-icon>
                  </v-btn>
                </template>
                <span>Manage Tiers</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="success"
                    v-bind="attrs"
                    v-on="on"
                    @click="viewEntries(item)"
                  >
                    <v-icon small class="material-icons-round">visibility</v-icon>
                  </v-btn>
                </template>
                <span>View Entries</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    :color="item.status === 'active' ? 'warning' : 'success'"
                    v-bind="attrs"
                    v-on="on"
                    @click="toggleStatus(item)"
                  >
                    <v-icon small class="material-icons-round">
                      {{ item.status === 'active' ? 'pause' : 'play_arrow' }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ item.status === 'active' ? 'Pause' : 'Activate' }}</span>
              </v-tooltip>

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    color="error"
                    v-bind="attrs"
                    v-on="on"
                    @click="deleteCampaign(item)"
                  >
                    <v-icon small class="material-icons-round">delete</v-icon>
                  </v-btn>
                </template>
                <span>Delete Campaign</span>
              </v-tooltip>
            </div>
          </template>
          </v-data-table>
        </v-card>
      </v-container>

      <!-- Create/Edit Campaign Dialog -->
      <campaign-dialog
        v-model="showCampaignDialog"
        :campaign="selectedCampaign"
        :is-edit="isEditMode"
        @saved="handleCampaignSave"
        @close="closeCampaignDialog"
      />

      <!-- Tiers Management Dialog -->
      <tiers-dialog
        v-model="showTiersDialog"
        :campaign="selectedCampaign"
        @close="closeTiersDialog"
        @refresh="loadCampaigns"
      />

      <!-- Entries View Dialog -->
      <entries-dialog
        v-model="showEntriesDialog"
        :campaign="selectedCampaign"
        @close="closeEntriesDialog"
      />
    </v-main>
    <vue-footer-admin></vue-footer-admin>
  </v-app>
</template>

<script>
import CampaignDialog from './SweepstakesCampaignDialog.vue'
import TiersDialog from './SweepstakesTiersDialog.vue'
import EntriesDialog from './SweepstakesEntriesDialog.vue'

export default {
  name: 'SweepstakesAdmin',
  components: {
    CampaignDialog,
    TiersDialog,
    EntriesDialog
  },
  data() {
    return {
      loading: false,
      search: '',
      campaigns: [],
      stats: {
        totalCampaigns: 0,
        activeCampaigns: 0,
        totalEntries: 0,
        totalRevenue: 0
      },
      referralStats: {
        total_referrals: 0,
        signup_referrals: 0,
        entry_referrals: 0,
        total_referral_value: 0,
        top_referrers: []
      },
      showCampaignDialog: false,
      showTiersDialog: false,
      showEntriesDialog: false,
      selectedCampaign: null,
      isEditMode: false,
      headers: [
        { text: 'Title', value: 'title', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Prize Value', value: 'prize_value', sortable: true },
        { text: 'Start Date', value: 'start_date', sortable: true },
        { text: 'End Date', value: 'end_date', sortable: true },
        { text: 'Entries', value: 'total_entries', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false, width: '200px' }
      ]
    }
  },
  computed: {
    activeCampaignWithTarget() {
      return this.campaigns.find(campaign =>
        campaign.status === 'active' &&
        campaign.cape_price &&
        campaign.cape_price > 0
      )
    },

    targetProgressPercentage() {
      if (!this.activeCampaignWithTarget) return 0
      const current = this.activeCampaignWithTarget.current_revenue || 0
      const target = this.activeCampaignWithTarget.cape_price || 1
      return Math.min((current / target) * 100, 100)
    },

    targetProgressColor() {
      const percentage = this.targetProgressPercentage
      if (percentage >= 50) return 'green'
      if (percentage >= 25) return 'yellow'
      return 'red'
    }
  },
  async mounted() {
    try {
      await this.loadCampaigns()
      await this.loadStats()
      await this.loadReferralStats()
    } catch (error) {
      console.error('Error initializing sweepstakes admin:', error)
    }
  },
  methods: {
    async loadCampaigns() {
      try {
        this.loading = true
        const response = await window.axios.get('/api/sweepstakes-admin/campaigns', {
          headers: {
            Authorization: "Bearer " + localStorage.getItem('token')
          }
        })
        if (response.data.flag === 1) {
          this.campaigns = response.data.campaigns.map(campaign => ({
            ...campaign,
            // Backend already returns total_entries, no need to map from entries_count
            status: campaign.active ? 'active' : 'inactive'
          }))
        } else {
          this.campaigns = []
        }
      } catch (error) {
        console.error('Error loading campaigns:', error)
        this.$toast.error('Failed to load campaigns')
        this.campaigns = []
      } finally {
        this.loading = false
      }
    },

    async loadStats() {
      try {
        // Load real stats from API
        const response = await window.axios.get('/api/sweepstakes-admin/stats', {
          headers: {
            Authorization: "Bearer " + localStorage.getItem('token')
          }
        })

        if (response.data.flag === 1) {
          this.stats = response.data.stats
        } else {
          // Fallback to calculating from campaigns data
          const totalCampaigns = this.campaigns.length
          const activeCampaigns = this.campaigns.filter(c => c.status === 'active').length
          const totalEntries = this.campaigns.reduce((sum, c) => sum + (c.total_entries || 0), 0)

          this.stats = {
            totalCampaigns,
            activeCampaigns,
            totalEntries,
            totalRevenue: 0
          }
        }
      } catch (error) {
        console.error('Error loading stats:', error)
        // Fallback stats
        this.stats = {
          totalCampaigns: 0,
          activeCampaigns: 0,
          totalEntries: 0,
          totalRevenue: 0
        }
      }
    },

    async loadReferralStats() {
      try {
        const response = await window.axios.get('/api/sweepstakes-admin/referrals/stats', {
          headers: {
            Authorization: "Bearer " + localStorage.getItem('token')
          }
        })

        if (response.data.flag === 1) {
          this.referralStats = response.data.stats
        } else {
          this.referralStats = {
            total_referrals: 0,
            signup_referrals: 0,
            entry_referrals: 0,
            total_referral_value: 0,
            top_referrers: []
          }
        }
      } catch (error) {
        console.error('Error loading referral stats:', error)
        this.referralStats = {
          total_referrals: 0,
          signup_referrals: 0,
          entry_referrals: 0,
          total_referral_value: 0,
          top_referrers: []
        }
      }
    },

    openCreateDialog() {
      this.selectedCampaign = null
      this.isEditMode = false
      this.showCampaignDialog = true
    },

    editCampaign(campaign) {
      this.selectedCampaign = { ...campaign }
      this.isEditMode = true
      this.showCampaignDialog = true
    },

    manageTiers(campaign) {
      this.selectedCampaign = campaign
      this.showTiersDialog = true
    },

    viewEntries(campaign) {
      this.selectedCampaign = campaign
      this.showEntriesDialog = true
    },

    async toggleStatus(campaign) {
      try {
        // Campaign status API not available yet
        this.$toast.error('Campaign status API not implemented yet')
        // TODO: Implement actual API call when admin endpoints are available
      } catch (error) {
        console.error('Error updating status:', error)
        this.$toast.error('Failed to update campaign status')
      }
    },

    async deleteCampaign(campaign) {
      if (await this.$confirm('Are you sure you want to delete this campaign? This action cannot be undone.')) {
        try {
          // Campaign deletion API not available yet
          this.$toast.error('Campaign deletion API not implemented yet')
          // TODO: Implement actual API call when admin endpoints are available
        } catch (error) {
          console.error('Error deleting campaign:', error)
          this.$toast.error('Failed to delete campaign')
        }
      }
    },

    async handleCampaignSave() {
      await this.loadCampaigns()
      await this.loadStats()
      await this.loadReferralStats()
      this.closeCampaignDialog()
    },

    closeCampaignDialog() {
      this.showCampaignDialog = false
      this.selectedCampaign = null
      this.isEditMode = false
    },

    closeTiersDialog() {
      this.showTiersDialog = false
      this.selectedCampaign = null
    },

    closeEntriesDialog() {
      this.showEntriesDialog = false
      this.selectedCampaign = null
    },

    getStatusColor(status) {
      switch (status) {
        case 'active': return 'success'
        case 'inactive': return 'warning'
        case 'completed': return 'info'
        case 'draft': return 'grey'
        default: return 'grey'
      }
    },

    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      })
    },

    formatNumber(value) {
      if (!value) return '0'
      return new Intl.NumberFormat('en-US').format(value)
    }
  }
}
</script>

<style scoped>
/* Use existing admin styles */
.card-shadow {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.border-radius-xl {
  border-radius: 1rem;
}

.text-typo {
  color: #344767;
}

.material-icons-round {
  font-family: 'Material Icons Round';
}
</style>
